import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, pushSegment } from '../glyphs/frame';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { t } from '../../translation/markers';

/** Segments approximating the basin ellipse. */
const BASIN_SEGMENTS = 20;

/**
 * Sink (washbasin): a fixed-size wall fixture (POINT anchor + WALL constraint)
 * — a single click snaps it flush against the nearest wall face and it slides
 * along the wall on drag. Drawn parametrically: counter outline, elliptical
 * basin and a tap mark against the wall (local -Y).
 */
function buildSinkGlyph(frame: SketchObjectFrame): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  // Counter outline: the full footprint.
  pushRect(out, frame, -hw, hw, -hd, hd);

  // Basin: an ellipse inset from the counter, biased slightly toward the room.
  const basinRadiusU = hw * 0.7;
  const basinCenterN = hd * 0.05;
  const basinRadiusN = hd * 0.65;
  let prev: Pt2 | null = null;
  for (let i = 0; i <= BASIN_SEGMENTS; i++) {
    const t = (2 * Math.PI * i) / BASIN_SEGMENTS;
    const pt = at(frame, basinRadiusU * Math.cos(t), basinCenterN + basinRadiusN * Math.sin(t));
    if (prev) pushSegment(out, prev, pt, frame.z);
    prev = pt;
  }

  // Tap: a small box on the wall side, centered, with a short spout line
  // toward the basin.
  const tapHalfU = hw * 0.12;
  pushRect(out, frame, -tapHalfU, tapHalfU, -hd, -hd + frame.depth * 0.12);
  pushSegment(out, at(frame, 0, -hd + frame.depth * 0.12), at(frame, 0, basinCenterN - basinRadiusN), frame.z);

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'sink',
    label: t('Sink'),
    category: SketchObjectCategory.BATHROOM,
    anchor: SketchObjectAnchorKind.POINT,
    constraint: { kind: 'WALL', defaultInset: 0 },
    defaultSize: { width: 0.6, depth: 0.45 },
    params: {},
    glyph: { kind: 'parametric', build: buildSinkGlyph },
  }),
);
